/* Wishlist page — fetches saved products via the Section Rendering API,
   renders cards, and drives filter/sort/empty-state/removal/add-to-cart.

   Depends on window.CaratKinWishlist (theme/assets/wishlist.js) being loaded
   first. That script owns the localStorage list; this script only reads/uses it
   for page rendering.

   Architecture decision (see WISHLIST_NOTES.md): we fetch each product's card
   through /products/{handle}?section_id=wishlist-card, which returns current
   server-rendered markup with live prices and images. This avoids duplicating
   card HTML in two places and keeps prices fresh.
*/

(() => {
  const SECTION_ID = "wishlist-card";
  const grid = document.querySelector("[data-wishlist-grid]");
  const empty = document.querySelector("[data-wishlist-empty]");
  const countBadge = document.querySelector("[data-wishlist-count]");

  if (!grid) return;

  let cards = []; // { id, handle, category, price, added (index), element }
  let activeFilter = "all";
  let activeSort = "recent";

  /* ── Fetch & render ─────────────────────────────────────────────────── */

  async function fetchCard(handle) {
    try {
      const res = await fetch(`/products/${handle}?section_id=${SECTION_ID}`);
      if (!res.ok) return null;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.querySelector(".wishlist-card");
    } catch {
      return null;
    }
  }

  async function loadCards() {
    const wl = window.CaratKinWishlist;
    if (!wl) return;

    const ids = wl.read();
    const handles = wl.readHandles();

    // Only items that have a handle can be fetched. Items saved before the
    // handle extension will be missing — noted in WISHLIST_NOTES.md.
    const entries = ids
      .map((id, index) => ({ id, handle: handles[id], added: ids.length - index }))
      .filter((e) => e.handle);

    if (entries.length === 0) {
      showEmpty();
      return;
    }

    grid.innerHTML = "";
    grid.hidden = false;
    empty.hidden = true;

    // Fetch all cards in parallel for speed.
    const results = await Promise.all(
      entries.map(async (entry) => {
        const el = await fetchCard(entry.handle);
        if (!el) return null;
        const price = parseInt(el.dataset.itemPrice || "0", 10);
        const category = (el.dataset.itemCategory || "").toLowerCase();
        return { id: entry.id, handle: entry.handle, category, price, added: entry.added, element: el };
      })
    );

    cards = results.filter(Boolean);
    renderGrid();
    updateCount(ids.length);
  }

  /* ── Render helpers ─────────────────────────────────────────────────── */

  function getVisibleCards() {
    const filtered = activeFilter === "all"
      ? [...cards]
      : cards.filter((c) => c.category === activeFilter);

    return filtered.sort((a, b) => {
      if (activeSort === "price-low") return a.price - b.price;
      if (activeSort === "price-high") return b.price - a.price;
      return b.added - a.added; // recent first
    });
  }

  function renderGrid() {
    const visible = getVisibleCards();
    grid.replaceChildren(...visible.map((c) => c.element));
    grid.hidden = visible.length === 0;
    empty.hidden = visible.length !== 0 || cards.length !== 0;

    // When filter produces no results but the list is not empty, keep grid hidden
    // and show nothing (no empty state — the user can change the filter).
    if (visible.length === 0 && cards.length === 0) {
      showEmpty();
    }

    attachCardActions();
  }

  function showEmpty() {
    grid.hidden = true;
    grid.innerHTML = "";
    empty.hidden = false;
    updateCount(0);
  }

  function updateCount(count) {
    if (!countBadge) return;
    countBadge.textContent = `${count} ${count === 1 ? "item" : "items"}`;
  }

  /* ── Card actions ───────────────────────────────────────────────────── */

  function attachCardActions() {
    grid.querySelectorAll("[data-remove-wishlist]").forEach((button) => {
      // Avoid double-binding.
      if (button._wishlistBound) return;
      button._wishlistBound = true;

      button.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const card = button.closest("[data-item-id]");
        const id = card && card.dataset.itemId;
        if (!id) return;

        // Use the shared wishlist API to remove.
        window.CaratKinWishlist.toggle(id);
        cards = cards.filter((c) => c.id !== id);
        renderGrid();
        updateCount(window.CaratKinWishlist.count());
      });
    });

    grid.querySelectorAll("[data-wishlist-cart]").forEach((button) => {
      if (button._cartBound) return;
      button._cartBound = true;

      button.addEventListener("click", async (e) => {
        e.preventDefault();
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        const items = [{ id: parseInt(variantId, 10), quantity: 1 }];
        // Guests sign in first; auth-gate.js adds this line once they are back.
        if (window.CaratKinAuthGate?.intercept({ kind: "cart", items }, () => button.click())) return;

        button.disabled = true;
        try {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items }),
          });
          if (res.ok) {
            button.querySelector("span").textContent = "Added";
            if (window.CaratKinCartDrawer) {
              window.CaratKinCartDrawer.refresh();
              window.CaratKinCartDrawer.open();
            }
          }
        } catch {
          /* silent — button stays disabled as feedback */
        }
      });
    });
  }

  /* ── Toolbar ────────────────────────────────────────────────────────── */

  function setupToolbar() {
    document.querySelectorAll("[data-toolbar-toggle]").forEach((button) => {
      button.addEventListener("click", () => {
        const type = button.dataset.toolbarToggle;
        const menu = document.querySelector(`[data-toolbar-menu="${type}"]`);
        const willOpen = menu.hidden;
        document.querySelectorAll("[data-toolbar-menu]").forEach((m) => { m.hidden = true; });
        document.querySelectorAll("[data-toolbar-toggle]").forEach((b) => { b.setAttribute("aria-expanded", "false"); });
        menu.hidden = !willOpen;
        button.setAttribute("aria-expanded", String(willOpen));
      });
    });

    document.querySelectorAll("[data-filter]").forEach((button) => {
      button.addEventListener("click", () => {
        activeFilter = button.dataset.filter;
        button.closest("[data-toolbar-menu]").hidden = true;
        button.closest(".wishlist-toolbar__control").querySelector("[data-toolbar-toggle]").setAttribute("aria-expanded", "false");
        renderGrid();
      });
    });

    document.querySelectorAll("[data-sort]").forEach((button) => {
      button.addEventListener("click", () => {
        activeSort = button.dataset.sort;
        document.querySelector("[data-sort-label]").textContent = button.textContent;
        button.closest("[data-toolbar-menu]").hidden = true;
        button.closest(".wishlist-toolbar__control").querySelector("[data-toolbar-toggle]").setAttribute("aria-expanded", "false");
        renderGrid();
      });
    });
  }

  /* ── Init ───────────────────────────────────────────────────────────── */

  setupToolbar();
  loadCards();

  // Re-render if the wishlist changes from another tab.
  document.addEventListener("carat-kin:wishlist-change", () => loadCards());
})();
